import React from 'react';
import { HelpCircle, FileText, Calculator, Truck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useLanguage } from '../context/LanguageContext';

const InformationPage = () => {
  const { language } = useLanguage();

  const faqSections = language === 'fr' ? [
    {
      icon: FileText,
      title: "Conception et plans structuraux",
      questions: [
        {
          q: "Vos plans de structure sont-ils scellés par un expert ?",
          a: "Oui. Tous nos plans sont approuvés et scellés par un ingénieur membre de l'Ordre des ingénieurs du Québec (OIQ) et de l'Ontario (OIO). Cette certification garantit leur conformité et leur acceptation par l'ensemble des municipalités du Québec et de l'Ontario."
        },
        {
          q: "Quels types de bâtiments couvrez-vous ?",
          a: "Nous réalisons des plans structuraux pour les projets, multifamiliaux, agrandissements et surélévations, retrait ou ajout de murs porteurs, ajout de poutres, colonnes ou ouvertures, garages, mezzanines et escaliers structuraux et pour finir des rénovations nécessitant une évaluation structurelle."
        },
        {
          q: "Combien de temps faut-il pour recevoir les plans ?",
          a: "Les délais varient selon la complexité du projet. En général, un plan standard est livré entre 2 à 4 semaines après la réception de toutes les informations requises."
        },
        {
          q: "Est-ce possible de modifier un plan existant ?",
          a: "Oui. Nous pouvons adapter et réviser vos plans existants pour répondre à de nouvelles exigences structurales."
        },
        {
          q: "Travaillez-vous selon le Code du bâtiment du Québec et du Canada ?",
          a: "Oui. Tous nos designs sont réalisés conformément : au Code de construction du Québec, au Code national du bâtiment du Canada ainsi qu'aux normes applicables (CSA, NBC, etc.)"
        }
      ]
    },
    {
      icon: Calculator,
      title: "Estimations de coûts",
      questions: [
        {
          q: "Offrez-vous des services d'estimation budgétaire pour les projets ?",
          a: "Absolument. Nous réalisons des estimations détaillées de matériaux et de main-d'œuvre à chaque phase du projet — conception, appel d'offres ou exécution."
        },
        {
          q: "Quelle est la précision de vos estimations ?",
          a: "Nos estimations sont fondées sur des bases de données actualisées et des logiciels de pointe (BIM & quantités automatisées). Elles atteignent généralement une précision de ±5 % à 10 % selon le niveau d'information fourni."
        },
        {
          q: "Fournissez-vous des rapports d'estimation complets ?",
          a: "Oui. Chaque estimation est accompagnée d'un rapport clair présentant les quantités, les coûts unitaires, et les totaux par catégorie de travaux, facilitant ainsi la prise de décision."
        },
        {
          q: "Que comprend votre service de gestion de projet ?",
          a: "Nous offrons une assistance complète : coordination entre les parties prenantes (architectes ou entrepreneurs), suivi d'avancement, contrôle des coûts et gestion des changements."
        },
        {
          q: "Travaillez-vous avec des entrepreneurs ou des clients privés ?",
          a: "Les deux. Nous collaborons autant avec des entrepreneurs généraux qu'avec des propriétaires privés souhaitant un accompagnement technique et administratif professionnel."
        },
        {
          q: "Offrez-vous un suivi après la livraison du plan ou du devis ?",
          a: "Oui. Nous restons disponibles pour tout support technique, ajustement ou coordination nécessaire jusqu'à la phase de construction."
        }
      ]
    },
    {
      icon: Truck,
      title: "Livraison et processus",
      questions: [
        {
          q: "Comment se déroule la livraison des documents ?",
          a: "Les plans et estimations sont transmis en format numérique (PDF, DWG, Excel) par courriel ou via un lien sécurisé. Des copies imprimées peuvent être fournies sur demande."
        },
        {
          q: "Quels sont vos modes de paiement ?",
          a: "Les paiements se font par virement bancaire, selon les informations fournies dans l'accord de paiement joint à nos propositions."
        },
        {
          q: "Pouvez-vous intervenir à distance ?",
          a: "Oui. Grâce à nos outils numériques, nous réalisons des projets partout au Canada, tout en maintenant une communication fluide et un haut niveau de qualité."
        }
      ]
    }
  ] : [
    {
      icon: FileText,
      title: "Design and Structural Plans",
      questions: [
        {
          q: "Are your structural plans sealed by an expert?",
          a: "Yes. All our plans are approved and sealed by an engineer member of the Ordre des ingénieurs du Québec (OIQ) and Ontario (OIO). This certification guarantees their compliance and acceptance by all Quebec and Ontario municipalities."
        },
        {
          q: "What types of buildings do you cover?",
          a: "We create structural plans for residential projects, multi-family buildings, extensions and additions, removal or addition of load-bearing walls, addition of beams, columns or openings, garages, mezzanines and structural stairs, and renovations requiring structural evaluation."
        },
        {
          q: "How long does it take to receive the plans?",
          a: "Timelines vary depending on project complexity. Generally, a standard residential plan is delivered within 2 to 4 weeks after receiving all required information."
        },
        {
          q: "Is it possible to modify an existing plan?",
          a: "Yes. We can adapt and revise your existing plans to meet new structural requirements."
        },
        {
          q: "Do you work according to the Quebec and Canada Building Code?",
          a: "Yes. All our designs are made in accordance with: the Quebec Construction Code, the National Building Code of Canada, and applicable standards (CSA, NBC, etc.)"
        }
      ]
    },
    {
      icon: Calculator,
      title: "Cost Estimates",
      questions: [
        {
          q: "Do you offer budget estimation services for projects?",
          a: "Absolutely. We provide detailed material and labor estimates at each project phase — design, tendering, or execution."
        },
        {
          q: "How accurate are your estimates?",
          a: "Our estimates are based on updated databases and cutting-edge software (BIM & automated quantities). They generally achieve an accuracy of ±5% to 10% depending on the level of information provided."
        },
        {
          q: "Do you provide complete estimation reports?",
          a: "Yes. Each estimate comes with a clear report presenting quantities, unit costs, and totals by work category, facilitating decision-making."
        },
        {
          q: "What does your project management service include?",
          a: "We offer complete assistance: coordination between stakeholders (architects or contractors), progress monitoring, cost control, and change management."
        },
        {
          q: "Do you work with contractors or private clients?",
          a: "Both. We collaborate with general contractors as well as private owners seeking professional technical and administrative support."
        },
        {
          q: "Do you offer follow-up after plan or quote delivery?",
          a: "Yes. We remain available for any technical support, adjustment, or coordination needed until the construction phase."
        }
      ]
    },
    {
      icon: Truck,
      title: "Delivery and Process",
      questions: [
        {
          q: "How are documents delivered?",
          a: "Plans and estimates are transmitted in digital format (PDF, DWG, Excel) by email or via a secure link. Printed copies can be provided upon request."
        },
        {
          q: "What are your payment methods?",
          a: "Payments are made by bank transfer, according to the information provided in the payment agreement attached to our proposals."
        },
        {
          q: "Can you work remotely?",
          a: "Yes. Thanks to our digital tools, we complete projects across Canada while maintaining smooth communication and a high level of quality."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80" 
            alt="FAQ Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/85" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/20 text-blue-300 text-sm font-medium mb-6">
            <HelpCircle className="w-4 h-4 inline mr-2" />
            F.A.Q.
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Foire aux questions' : 'Frequently Asked Questions'}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {language === 'fr' 
              ? 'Trouvez les réponses à vos questions sur nos services d\'ingénierie structurale'
              : 'Find answers to your questions about our structural engineering services'}
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-12">
            {faqSections.map((section, sectionIdx) => (
              <div key={sectionIdx} className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <section.icon className="w-6 h-6 text-white" />
                    <h2 className="text-xl font-bold text-white">{section.title}</h2>
                  </div>
                </div>
                <div className="p-6">
                  <Accordion type="single" collapsible className="space-y-2">
                    {section.questions.map((item, idx) => (
                      <AccordionItem 
                        key={idx} 
                        value={`section-${sectionIdx}-item-${idx}`}
                        className="border border-slate-200 rounded-xl px-4 data-[state=open]:bg-blue-50/50"
                      >
                        <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-blue-700 py-4">
                          <span className="flex items-start gap-3">
                            <span className="text-blue-500 font-bold">{idx + 1}.</span>
                            {item.q}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-slate-600 leading-relaxed pb-4 pl-7">
                          {item.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InformationPage;
